import React, {useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Spinner from '../components/common/Spinner';
import Button from '../components/common/Button';
import useScrollToTop from '../hooks/hook';
import { api } from '../config';
import { useError, withErrorHandling } from '../components/common/ErrorHandling';

// フォームデータの型定義
interface FormData {
  name: string;
  affiliation: string;
  email: string;
  phone: string;
  feedback: string;
  reason: string;
  reasonDetail?: string;
  interest: string;
  services: string[];
  otherServiceDetail?: string;
  comment: string;
}

const ContactPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    affiliation: '',
    email: '',
    phone: '',
    feedback: '',
    reason: '',
    reasonDetail: '',
    interest: '',
    services: [],
    otherServiceDetail: '',
    comment: '',
  });

  const { setError } = useError();
  const navigate = useNavigate();
  useScrollToTop();

  const userId = sessionStorage.getItem('userId') || '';
  const results = sessionStorage.getItem('results') || '';

  // 入力変更ハンドラ
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleServicesChange = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  // フォーム送信
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      await api.postContact({ ...formData, userId });
      await api.postMail({ ...formData, userId }, results);
      navigate('/thanks');
    } catch (error) {
      setError('フォーム送信中に問題が発生しました。再読み込みしてください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="l_container">
      <Header />
      <div className="l_contact">
        <div className="contact-form">
          {loading ? (
            <Spinner />
          ) : (
            <>
              <p className="contact_page_desc">
                「挑戦力診断WATARE」についてアンケートにご協力ください。<br />
                ※弊社
                <a href="/Privacypolicy.pdf" target="_blank" rel="noopener noreferrer">
                  「プライバシーポリシー」
                </a>
                をご一読いただき、同意の上で以下にご記入ください。
              </p>
              <form onSubmit={handleSubmit}>
                <div className="l_form-group">
                  {[
                    { id: 'name', label: 'お名前' },
                    { id: 'affiliation', label: 'ご所属（企業名・学校名など）' },
                    { id: 'email', label: 'メールアドレス', type: 'email' },
                    { id: 'phone', label: 'お電話番号', type: 'tel' },
                  ].map(({ id, label, type = 'text' }) => (
                    <div className="form-group" key={id}>
                      <label htmlFor={id}>{label}</label>
                      <input
                        id={id}
                        name={id}
                        type={type}
                        value={formData[id as keyof FormData]}
                        onChange={handleChange}
                      />
                    </div>
                  ))}
                  <div className="form-group">
                    <label>「挑戦力診断WATARE」お試し版の判定結果をご覧になった感想をお聞かせください</label>
                    <div className="radio-group">
                      {['納得感がある', 'ある程度納得できる', 'どちらともいえない', 'あまり納得できない', '全く納得できない'].map((option) => (
                        <label key={option}>
                          <input
                            type="radio"
                            name="feedback"
                            value={option}
                            checked={formData.feedback === option}
                            onChange={handleChange}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>「挑戦力診断WATARE」をお試しいただいたきっかけをお聞かせください</label>
                    <div className="radio-group">
                      {['弊社スタッフからの紹介', '人からの紹介', 'セミナーでの紹介', 'WEB検索', 'SNS', 'その他(自由記述)'].map((option) => (
                        <label key={option}>
                          <input
                            type="radio"
                            name="reason"
                            value={option}
                            checked={formData.reason === option}
                            onChange={handleChange}
                          />
                          {option}
                        </label>
                      ))}
                      {formData.reason === 'その他(自由記述)' && (
                        <textarea
                          name="reasonDetail"
                          placeholder="その他の理由を入力してください"
                          value={formData.reasonDetail || ''}
                          onChange={handleChange}
                          className="textarea"
                        />
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>正式版「挑戦力診断WATARE」へのご興味の度合いをお聞かせください</label>
                    <div className="radio-group">
                      {['パンフレットを送ってほしい', 'サービスについて説明を聞きたい', '現状、正式版に興味はない'].map((option) => (
                        <label key={option}>
                          <input
                            type="radio"
                            name="interest"
                            value={option}
                            checked={formData.interest === option}
                            onChange={handleChange}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>弊社のサービスで他にご興味があるものについてお聞かせください</label>
                    <div className="checkbox-group">
                      {[
                        '挑戦力開発セミナー',
                        'アントレプレナーシップ開発セミナー',
                        'キャリアデザインセミナー',
                        'リーダーシップ開発セミナー',
                        'LEGO® SERIOUS PLAY®メソッドと教材を活用したワークショップ',
                        'コーチング',
                        'コンサルティング',
                        'その他(自由記述)',
                      ].map((option) => (
                        <label key={option}>
                          <input
                            type="checkbox"
                            name="services"
                            value={option}
                            checked={formData.services.includes(option)}
                            onChange={() => handleServicesChange(option)}
                          />
                          {option}
                        </label>
                      ))}
                      {formData.services.includes('その他(自由記述)') && (
                        <textarea
                          name="otherServiceDetail"
                          placeholder="その他のサービスを入力してください"
                          value={formData.otherServiceDetail || ''}
                          onChange={handleChange}
                          className="textarea"
                        />
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="comment">その他何かあればご記入ください</label>
                    <textarea
                      id="comment"
                      name="comment"
                      value={formData.comment}
                      onChange={handleChange}
                      className="textarea"
                    />
                  </div>
                </div>
                <p className="contact_page_txt">
                  入力内容の確認画面は出ません。<br />
                  送信の前に内容をご確認ください。
                </p>
                <Button onClick={() => { }} className="custom" iconType="right" iconSize={30}>
                  上記の内容で送信する
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;
